import React, { useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import SideNav from './SideNav';
import MobileNav from './MobileNav';
import SandboxEnvironmentBanner from '../shared/banners/SandboxEnvironmentBanner';
import TrialBanner from '../shared/banners/TrialBanner';
import VesPaymentReminderBanner from '../shared/banners/VesPaymentReminderBanner';
import FiscalDataBanner from '../shared/banners/FiscalDataBanner';
import ProfitPlus2k8BridgeBanner from '../shared/banners/ProfitPlus2k8BridgeBanner';
import ContactButton from '../shared/contact/ContactButton';
import IntelligenceChatShell from '../intelligence/IntelligenceChatShell';
import AdminTopBar, { ADMIN_TOPBAR_HEIGHT } from './AdminTopBar';
import { OnboardingProvider } from '../onboarding/OnboardingContext';
import OnboardingRail from '../onboarding/OnboardingRail';
import FirstSaleCelebration from '../onboarding/FirstSaleCelebration';
import { Organization } from '../../models';

interface MainLayoutProps {
  organization: Organization | null;
  userOrganizations: Organization[];
  onOrganizationChange: (organizationId: string) => void;
  device: string;
  navStatus: string;
  setNavStatus: (status: string) => void;
  isAdmin?: boolean;
  panelPermissions?: string[] | null;
  isGodMode?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  organization,
  userOrganizations,
  onOrganizationChange,
  device,
  navStatus,
  setNavStatus,
  isAdmin = false,
  panelPermissions = null,
  isGodMode = false,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme } = useTheme();
  const sidebarWidth = navStatus === 'expanded' ? '280px' : '88px';

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close mobile menu when device changes to desktop
  useEffect(() => {
    if (device !== 'small') {
      setIsMobileMenuOpen(false);
    }
  }, [device]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (device === 'small' && isMobileMenuOpen) {
        const target = event.target as Element;
        if (
          !target.closest('.mobile-nav') &&
          !target.closest('.mobile-menu-toggle')
        ) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [device, isMobileMenuOpen]);

  const sandboxShell = organization?.isSandbox === true;
  const sandboxBg =
    theme === 'dark'
      ? 'linear-gradient(165deg, #0b1220 0%, #111827 42%, #0f172a 100%)'
      : 'linear-gradient(165deg, #f8fafc 0%, #f1f5f9 42%, #e8eef4 100%)';
  const sandboxInset =
    theme === 'dark'
      ? 'inset 0 0 0 1px rgba(71, 85, 105, 0.45)'
      : 'inset 0 0 0 1px rgba(148, 163, 184, 0.35)';

  return (
    <OnboardingProvider organizationId={organization?.id}>
    <div
      className="d-flex position-relative bg-body"
      style={{
        minHeight: '100vh',
        background: sandboxShell ? sandboxBg : 'var(--bs-body-bg)',
        overflow: 'hidden',
        boxShadow: sandboxShell ? sandboxInset : undefined,
      }}
    >
      {/* Mobile Navigation */}
      {device === 'small' && (
        <>
          <MobileNav
            organization={organization}
            isAdmin={isAdmin}
            panelPermissions={panelPermissions}
            isGodMode={isGodMode}
            isMenuOpen={isMobileMenuOpen}
            onToggleMenu={toggleMobileMenu}
          />

          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div
              className="position-fixed w-100 h-100 bg-dark"
              style={{
                top: 0,
                left: 0,
                zIndex: 1040,
                opacity: 0.5,
                transition: 'opacity 0.3s ease',
              }}
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </>
      )}

      {/* Desktop Sidebar */}
      {device !== 'small' && (
        <div
          className="position-fixed h-100"
          style={{
            left: '0',
            top: '0',
            zIndex: 1030,
            transition: 'transform 0.3s ease',
          }}
        >
          <SideNav
            setStatus={setNavStatus}
            organization={organization}
            userOrganizations={userOrganizations}
            onOrganizationChange={onOrganizationChange}
            isAdmin={isAdmin}
            panelPermissions={panelPermissions}
            isGodMode={isGodMode}
          />
        </div>
      )}

      {/* Main Content */}
      <div
        className="flex-grow-1"
        style={{
          marginLeft: device === 'small' ? '0' : sidebarWidth,
          transition: 'margin-left 0.3s ease',
          minHeight: '100vh',
          position: 'relative',
        }}
      >
        {device !== 'small' && (
          <AdminTopBar
            organizationId={organization?.id}
            sidebarWidth={sidebarWidth}
          />
        )}
        <div
          className="container-responsive"
          style={{
            paddingTop:
              device === 'small'
                ? '80px'
                : `calc(${ADMIN_TOPBAR_HEIGHT}px + var(--spacing-md))`,
            paddingBottom: 'var(--spacing-lg)',
            paddingLeft: 'var(--spacing-md)',
            paddingRight: 'var(--spacing-md)',
            overflowX: 'hidden',
          }}
        >
          <SandboxEnvironmentBanner organization={organization} />
          <TrialBanner organization={organization} />
          <VesPaymentReminderBanner organization={organization} />
          <FiscalDataBanner organization={organization} />
          <ProfitPlus2k8BridgeBanner organizationId={organization?.id ?? null} />
          <Outlet />
        </div>
      </div>

      <IntelligenceChatShell
        organizationId={organization?.id}
        organization={organization}
        panelPermissions={panelPermissions}
        isGodMode={isGodMode}
      />
      <ContactButton />
      <OnboardingRail />
      <FirstSaleCelebration />
    </div>
    </OnboardingProvider>
  );
};

export default MainLayout;
